import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Paper } from '@mui/material';

// Define types for state variables
type CompoundingFrequency = 'annually' | 'semi-annually' | 'quarterly' | 'monthly' | 'daily';

export default function InterestCalculation() {
  const [principal, setPrincipal] = useState<string>('600000');
  const [rate, setRate] = useState<string>('4.99'); // Annual interest rate in percentage
  const [time, setTime] = useState<string>('1'); // Time in years
  const [compoundingFrequency, setCompoundingFrequency] = useState<CompoundingFrequency>('annually');

  const [simpleInterest, setSimpleInterest] = useState<number | null>(null);
  const [totalSimpleAmount, setTotalSimpleAmount] = useState<number | null>(null);
  const [compoundInterest, setCompoundInterest] = useState<number | null>(null);
  const [totalCompoundAmount, setTotalCompoundAmount] = useState<number | null>(null);

  useEffect(() => {
    const p = parseFloat(principal);
    const r = parseFloat(rate) / 100; // Convert percentage to decimal
    const t = parseFloat(time);

    if (!isNaN(p) && !isNaN(r) && !isNaN(t) && p > 0 && r > 0 && t > 0) {
      // Simple Interest Calculation
      const si = p * r * t;
      setSimpleInterest(si);
      setTotalSimpleAmount(p + si);

      // Compound Interest Calculation
      let n: number;
      switch (compoundingFrequency) {
        case 'semi-annually':
          n = 2;
          break;
        case 'quarterly':
          n = 4;
          break;
        case 'monthly':
          n = 12;
          break;
        case 'daily':
          n = 365;
          break;
        case 'annually':
        default:
          n = 1;
          break;
      }
      const compoundAmount = p * Math.pow((1 + r / n), n * t);
      const ci = compoundAmount - p;
      setCompoundInterest(ci);
      setTotalCompoundAmount(compoundAmount);

    } else {
      setSimpleInterest(null);
      setTotalSimpleAmount(null);
      setCompoundInterest(null);
      setTotalCompoundAmount(null);
    }
  }, [principal, rate, time, compoundingFrequency]);

  const formatCurrency = (value: number | null) => {
    if (value === null || isNaN(value)) return '-';
    return value.toFixed(2);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Interest Calculation</Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Principal Amount (P)"
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Annual Interest Rate (R) %"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Time Period (T) in Years"
              type="number"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              fullWidth
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="compounding-frequency-label">Compounding Frequency (for CI)</InputLabel>
              <Select
                labelId="compounding-frequency-label"
                value={compoundingFrequency}
                label="Compounding Frequency (for CI)"
                onChange={(e) => setCompoundingFrequency(e.target.value as CompoundingFrequency)}
              >
                <MenuItem value="annually">Annually</MenuItem>
                <MenuItem value="semi-annually">Semi-Annually</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2 }}>Results:</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography variant="subtitle1" gutterBottom>Simple Interest (SI)</Typography>
              <Typography variant="body1">Interest Earned: {formatCurrency(simpleInterest)}</Typography>
              <Typography variant="body1">Total Amount (P + SI): {formatCurrency(totalSimpleAmount)}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography variant="subtitle1" gutterBottom>Compound Interest (CI)</Typography>
              <Typography variant="body1">Interest Earned: {formatCurrency(compoundInterest)}</Typography>
              <Typography variant="body1">Total Amount (P + CI): {formatCurrency(totalCompoundAmount)}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
